import { create } from 'zustand'
import { getCookie, setCookie, removeCookie } from '@/lib/cookies'

const ACCESS_TOKEN = 'thisisjustarandomstring'
const AUTH_DATA = 'auth_data'

interface RoleInfo {
  id: number
  name: string
  display_name: string
}

interface AuthUser {
  id: number
  username: string
  email: string | null
  role: RoleInfo | null
  permissions: string[]
  areas: string[]
}

interface AuthState {
  auth: {
    user: AuthUser | null
    role: RoleInfo | null
    permissions: string[]
    areas: string[]
    accessToken: string
    setUser: (user: AuthUser | null) => void
    setPermissions: (
      permissions: string[],
      areas: string[],
      role: RoleInfo | null
    ) => void
    setAccessToken: (accessToken: string) => void
    resetAccessToken: () => void
    reset: () => void
  }
}

export const useAuthStore = create<AuthState>()((set) => {
  const cookieState = getCookie(ACCESS_TOKEN)
  const initToken = cookieState ? JSON.parse(cookieState) : ''

  // Load saved auth data
  let savedData: {
    permissions: string[]
    areas: string[]
    role: RoleInfo | null
  } = {
    permissions: [],
    areas: [],
    role: null,
  }
  try {
    const saved = localStorage.getItem(AUTH_DATA)
    if (saved) {
      savedData = JSON.parse(saved)
    }
  } catch {
    // no-op
  }

  return {
    auth: {
      user: null,
      role: savedData.role,
      permissions: savedData.permissions,
      areas: savedData.areas,
      accessToken: initToken,
      setUser: (user) =>
        set((state) => ({ ...state, auth: { ...state.auth, user } })),
      setPermissions: (permissions, areas, role) =>
        set((state) => {
          // Save to localStorage for persistence
          localStorage.setItem(
            AUTH_DATA,
            JSON.stringify({ permissions, areas, role })
          )
          return { ...state, auth: { ...state.auth, permissions, areas, role } }
        }),
      setAccessToken: (accessToken) =>
        set((state) => {
          setCookie(ACCESS_TOKEN, JSON.stringify(accessToken))
          return { ...state, auth: { ...state.auth, accessToken } }
        }),
      resetAccessToken: () =>
        set((state) => {
          removeCookie(ACCESS_TOKEN)
          return { ...state, auth: { ...state.auth, accessToken: '' } }
        }),
      reset: () =>
        set((state) => {
          removeCookie(ACCESS_TOKEN)
          localStorage.removeItem(AUTH_DATA)
          localStorage.removeItem('auth_token')
          return {
            ...state,
            auth: {
              ...state.auth,
              user: null,
              accessToken: '',
              permissions: [],
              areas: [],
              role: null,
            },
          }
        }),
    },
  }
})
